import axios from "axios";

const BASE_URL = import.meta.env.VITE_STN_API_URL || "http://localhost:4000";

const api = axios.create({
  baseURL: `${BASE_URL}/stn`,
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
});

// Public API — no auth, no /stn prefix (used for NSQF public data)
const publicApi = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
});

// Attach stored token on every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("stn-token");
  if (token) config.headers["accessToken"] = token;
  return config;
});

// Unwrap the { status: 200, res: {...} } envelope
api.interceptors.response.use(
  (r) => r.data?.res ?? r.data,
  (err) => {
    const msg =
      err.response?.data?.err?.msg ||
      err.response?.data?.message ||
      err.message ||
      "Something went wrong";
    const customError = new Error(msg);
    // Mirrors AcademyNexus/skillNexus's client.js — without this, callers
    // like InternHiring.jsx's handleEval can't tell a CONSENT_REQUIRED 403
    // apart from any other failure, since the backend's err.errCode was
    // otherwise discarded here.
    customError.errCode = err.response?.data?.err?.errCode || null;
    customError.errData = err.response?.data?.err || {};
    customError.status = err.response?.status || 0;
    return Promise.reject(customError);
  }
);

// ── Auth ──────────────────────────────────────────────────────────────────────

export const register   = (body)  => api.post("/auth/register", body);
export const login      = (body)  => api.post("/auth/login", body);
export const getMe      = ()      => api.get("/auth/me");

// Terms of Service + Privacy Policy — mounted at the backend ROOT (/consent),
// not under /stn, so this goes through publicApi rather than `api`.
export const getSignupConsents = (via = "email") =>
  publicApi.get("/consent/public/signup-consents", { params: { via } }).then(unwrapPublic);

// The document that governs submitting company verification details at the
// end of onboarding. isPublic:false, so the request needs the founder's
// token even though it's on publicApi's un-prefixed base URL.
export const getOnboardingConsent = () => {
  const token = localStorage.getItem("stn-token");
  return publicApi
    .get("/consent/public/policy", {
      params: { gate: "STN_ONBOARDING_GATE" },
      headers: token ? { accessToken: token } : {},
    })
    .then(unwrapPublic);
};

// Forgot / reset password (OTP flow)
export const forgotPassword = (email)                 => api.post("/auth/forgot-password", { email });
export const resetPassword  = (email, otp, newPassword) => api.post("/auth/reset-password", { email, otp, newPassword });

// ── Verification ──────────────────────────────────────────────────────────────

export const verifyCIN  = (cin, companyName) => api.post("/verify/cin", { cin, companyName });
export const verifyGST  = (gst)              => api.post("/verify/gst", { gst });
export const verifyPAN  = (pan)              => api.post("/verify/pan", { pan });

// ── Onboarding ────────────────────────────────────────────────────────────────

export const saveStep   = (step, body) => api.post(`/onboarding/step/${step}`, body);
export const submitProfile = (consent) => api.post("/onboarding/submit", { consent });
export const getMyStartup  = ()        => api.get("/startup/me");

// ── Document upload ───────────────────────────────────────────────────────────

export const uploadDoc = (file, docType) => {
  const form = new FormData();
  form.append("file", file);
  form.append("docType", docType);
  return api.post("/upload/doc", form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 60000,
  });
};

export const uploadPhoto = (file) => {
  const form = new FormData();
  form.append("file", file);
  return api.post("/upload/photo", form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 60000,
  });
};

export const uploadVideo = (file) => {
  const form = new FormData();
  form.append("file", file);
  return api.post("/upload/video", form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000,
  });
};

// ── Mentor management ─────────────────────────────────────────────────────────

export const inviteMentor          = (body)               => api.post("/mentors/invite", body);
export const listMentors           = ()                    => api.get("/mentors");
export const resendMentorInvite    = (profileId)           => api.post(`/mentors/${profileId}/resend-invite`);
export const patchMentorVisibility = (profileId, isPublic) => api.patch(`/mentors/${profileId}/visibility`, { isPublic });
export const getMentorAnalytics    = (profileId)           => api.get(`/mentors/${profileId}/analytics`);
export const getMentorCalendar     = (profileId, from, to) => api.get(`/mentors/${profileId}/calendar`, { params: { from, to } });

// ── Profile updates ───────────────────────────────────────────────────────────

export const updateFounderProfile  = (body) => api.patch("/founder/profile", body);
export const registerOfflineCenter = (body) => api.post("/startup/offline-center", body);
export const updateStartupProfile  = (body) => api.patch("/startup/profile", body);
export const submitChangeRequest   = (body) => api.post("/startup/change-request", body);
export const listChangeRequests    = ()     => api.get("/startup/change-requests");
export const changePassword        = (body) => api.post("/auth/change-password", body);

// ── Company Seal & Digital Identity ──────────────────────────────────────────
export const getSealInfo      = ()          => api.get("/startup/seal");
export const generateSealKeys = ()          => api.post("/startup/seal/generate");
export const uploadSealImage  = (dataUrl)   => api.post("/upload/seal", { dataUrl });

export const uploadChangeDoc = (file) => {
  const form = new FormData();
  form.append("file", file);
  return api.post("/upload/change-doc", form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 60000,
  });
};

// ── Program Ideas ─────────────────────────────────────────────────────────────

export const createProgramIdea  = (body) => api.post("/ideas", body);
export const listProgramIdeas   = ()     => api.get("/ideas");
export const getProgramIdea     = (id)   => api.get(`/ideas/${id}`);

// ── Notifications ─────────────────────────────────────────────────────────────

export const listNotifications    = (params = {}) => api.get("/notifications", { params });
export const getUnreadCount       = ()             => api.get("/notifications/unread-count");
export const markNotifRead        = (id)           => api.patch(`/notifications/${id}/read`);
export const markAllNotifsRead    = ()             => api.patch("/notifications/read-all");
export const getNotifPreferences  = ()             => api.get("/notifications/preferences");
export const updateNotifPreferences = (body)       => api.patch("/notifications/preferences", body);
export const registerFcmToken     = (token)        => api.post("/auth/fcm-token", { token });
export const unregisterFcmToken   = (token)        => api.delete("/auth/fcm-token", { data: { token } });

// ── Gamification ──────────────────────────────────────────────────────────────

export const getGamificationWallet    = ()               => api.get("/gamification/wallet");
export const getGamificationLeaderboard = (params = {}) => api.get("/gamification/leaderboard", { params });
export const getMentorXP              = ()               => api.get("/gamification/mentors-xp");
export const convertStnxpToGnc        = ()               => api.post("/gamification/convert-gnc");

// ── Intern postings ───────────────────────────────────────────────────────────

export const listMyPostings      = (params = {})          => api.get("/interns/postings", { params });
export const getMyPosting        = (id)                   => api.get(`/interns/postings/${id}`);
export const createPosting       = (body)                 => api.post("/interns/postings", body);
export const updatePosting       = (id, body)             => api.patch(`/interns/postings/${id}`, body);
export const deletePosting       = (id)                   => api.delete(`/interns/postings/${id}`);
export const listApplications    = (params = {})          => api.get("/interns/applications", { params });
export const updateApplicationStage   = (id, body)          => api.patch(`/interns/applications/${id}/stage`, body);
export const getApplicationDetail     = (id)                => api.get(`/interns/applications/${id}`);
// A 15-minute link to an applicant's resume, while they still share their details with this startup.
export const getApplicantResume = (id) => api.get(`/interns/applications/${id}/resume`);
export const triggerAiEvaluation      = (id)                => api.post(`/interns/applications/${id}/ai-evaluate`);
// AI-eval consent (STN_AI_EVAL_GATE) — shown when triggerAiEvaluation 403s with CONSENT_REQUIRED.
export const requestAiEvalConsent     = (id)                => api.post(`/interns/applications/${id}/ai-consent-request`);
export const getAiEvalConsentStatus   = (id)                => api.get(`/interns/applications/${id}/ai-consent-status`);
export const getInternProgress        = (id)                => api.get(`/interns/applications/${id}/progress`);

// Candidate matching (Wave 1) — masked recommendation cards, never name/email/phone.
export const getRecommendedCandidates = (postingId)          => api.get(`/interns/postings/${postingId}/candidates`);
// Hiring phase 3: ask a hidden candidate to share their details; the resume of an approved request (15-minute link).
export const requestCandidateAccess = (postingId, ref, body) => api.post(`/interns/postings/${postingId}/candidates/${ref}/request-access`, body);
export const getAccessResume = (requestId) => api.get(`/interns/access-requests/${requestId}/resume`);
export const expressCandidateInterest = (postingId, studentRef) => api.post(`/interns/postings/${postingId}/candidates/${studentRef}/express-interest`);
export const submitCandidateFeedback  = (body)               => api.post("/interns/candidate-feedback", body);
export const getApplicationMatchScores = (postingId)         => api.get(`/interns/postings/${postingId}/applications/match-scores`);

// ── Webinar hosting (Wave 4) ────────────────────────────────────────────────────
export const inferStnWebinarIntent = (purposeText)        => api.post("/webinars/infer-intent", { purposeText });
export const createStnWebinar   = (body)                 => api.post("/webinars", body);
export const listStnWebinars    = ()                      => api.get("/webinars");
export const cancelStnWebinar   = (id, reason)             => api.patch(`/webinars/${id}/cancel`, { reason });
export const createStnWebinarRequest = (body)              => api.post("/webinars/requests", body);
export const listMyStnWebinarRequests = ()                => api.get("/webinars/requests");

// ── Founder dashboard ──────────────────────────────────────────────────────────

export const getFounderDashboard = () => api.get("/dashboard");

// ── Partnered batches (registration analytics shared from admin) ───────────────

export const getPartnerBatches        = ()        => api.get("/partner/batches");
export const getPartnerBatchAnalytics = (batchId) => api.get(`/partner/batches/${batchId}/analytics`);
export const getPartnerStudentDetail  = (batchId, studentId) => api.get(`/partner/batches/${batchId}/students/${studentId}`);
// Onboarding/coupon snapshot for the resend dialog + resend (optionally extending the coupon)
export const getStudentOnboardingInfo = (batchId, studentId) => api.get(`/partner/batches/${batchId}/students/${studentId}/onboarding-info`);
export const resendStudentPassword    = (batchId, studentId, body = {}) => api.post(`/partner/batches/${batchId}/students/${studentId}/resend-password`, body);
export const sendStudentResetLink     = (batchId, studentId) => api.post(`/partner/batches/${batchId}/students/${studentId}/reset-link`);
export const revokeStudentAccess      = (batchId, studentId, reason) => api.post(`/partner/batches/${batchId}/students/${studentId}/revoke`, { reason });
export const reassignStudentAccess    = (batchId, studentId) => api.post(`/partner/batches/${batchId}/students/${studentId}/reassign`);
export const updateStudentDetails     = (batchId, studentId, updates) => api.patch(`/partner/batches/${batchId}/students/${studentId}`, { updates });
export const getStudentChangeLog      = (batchId, studentId) => api.get(`/partner/batches/${batchId}/students/${studentId}/changes`);

// Application change requests (review queue)
export const getPartnerChangeRequests = () => api.get('/partner/change-requests');
export const reviewPartnerChangeRequest = (enrollmentId, reqId, action, note) => api.post(`/partner/change-requests/${enrollmentId}/${reqId}`, { action, note });
export const enableProgramChange = (batchId, studentId, enrollmentId, enable = true) =>
  api.post(`/partner/batches/${batchId}/students/${studentId}/program-change`, { enrollmentId, enable });

// Public TCI applications (no-login applicants for partner's TCI batches)
export const getPartnerTciApplications  = (status = "pending") => api.get("/partner/tci-applications", { params: { status } });
export const reviewPartnerTciApplication = (id, action, note) => api.post(`/partner/tci-applications/${id}/${action}`, { note });

// Day-wise attendance — roster for a date + bulk mark present/absent
export const getPartnerBatchAttendance  = (batchId, date) => api.get(`/partner/batches/${batchId}/attendance`, { params: date ? { date } : {} });
export const markPartnerBatchAttendance = (batchId, body) => api.post(`/partner/batches/${batchId}/attendance/mark`, body);

// Onboarding / coupon reshare — report + resend to not-activated / unused recipients
export const getPartnerCouponReport = (batchId) => api.get(`/partner/batches/${batchId}/coupon-report`);
export const reshareCouponPartner   = (code, body) => api.post(`/partner/coupon/${encodeURIComponent(code)}/reshare`, body);
// Batch-level onboarding/coupon reshare — uses each student's own coupon
export const reshareBatchCouponPartner = (batchId, body) => api.post(`/partner/batches/${batchId}/coupon-reshare`, body);

// ── Billing ───────────────────────────────────────────────────────────────────

export const getPlans          = ()               => api.get("/billing/plans");
export const getMySubscription = ()               => api.get("/billing/subscription");
export const getInvoices       = (params = {})    => api.get("/billing/invoices", { params });
export const createBillingOrder = (body)          => api.post("/billing/order", body);
export const verifyBillingPayment = (body)        => api.post("/billing/verify", body);
export const switchToFreePlan  = ()               => api.post("/billing/free");

// ── Academic dropdown data (no auth) — university / college / course stream / course ─
// Cascading lists shared with the InternNexus apply form (/student/in/public/*).
const unwrapPublic = (r) => r.data?.res ?? r.data;
export const getPublicUniversities = (params = {}) =>
  publicApi.get("/student/in/public/universities", { params }).then(unwrapPublic);
export const getPublicColleges     = (params = {}) =>
  publicApi.get("/student/in/public/colleges", { params }).then(unwrapPublic);
export const getPublicCourseGroups = () =>
  publicApi.get("/student/in/public/course-groups").then(unwrapPublic);
export const getPublicCourses      = (groupCode) =>
  publicApi.get("/student/in/public/courses", { params: { groupCode } }).then(unwrapPublic);

// PIN code -> city/state lookup (India Post, via the existing public proxy
// already used by the Ambassador Program's applicant forms) — reused here
// for the Ecosystem Goals step's regional-talent-pipeline location picker,
// not a new integration. Returns { success, data: { city, state, success } }.
export const resolvePincode = (pin) =>
  publicApi.get(`/ambassador/public/pincode/${pin}`).then(unwrapPublic);

// ── NSQF public data (no auth) ────────────────────────────────────────────────

export const getNsqfPublicGroups  = (params = {}) =>
  publicApi.get("/nsqf/public/course-groups", { params }).then(r => r.data?.res ?? r.data);

export const getNsqfPublicCourses = (params = {}) =>
  publicApi.get("/nsqf/public/courses", { params }).then(r => r.data?.res ?? r.data);

// ── Centralized Consent Manager (/consent) ────────────────────────────────────
// Mounted at the ROOT, not under /stn — it's one cross-platform surface shared
// with SkillNexus, InternNexus and PromptNexus, so it needs its own instance
// (the `api` above is pinned to /stn) that still carries the founder's token.
//
// The backend resolves WHO the caller is from that token: a founder's consents
// are filed under userType "founder" and the catalogue returns the rows that
// apply to founders, not to students. Nothing here says "founder" — that would
// be a claim the client isn't entitled to make.
const consentApi = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
});

consentApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("stn-token");
  if (token) config.headers["accessToken"] = token;
  return config;
});

consentApi.interceptors.response.use(
  (r) => r.data?.res ?? r.data,
  (err) =>
    Promise.reject(
      new Error(
        err.response?.data?.err?.msg ||
          err.response?.data?.message ||
          err.message ||
          "Something went wrong",
      ),
    ),
);

// Everything the signed-in founder can control, with their current answer.
export const getManageableConsents = () => consentApi.get("/consent/me/manageable");

// Grant / withdraw. Withdrawal may legitimately refuse (409) — the UI shows the
// server's reason rather than a generic failure.
export const grantConsent  = (data) => consentApi.post("/consent/me/grant", data);
export const revokeConsent = (data) => consentApi.post("/consent/me/revoke", data);

// The append-only trail: every grant and withdrawal, newest first.
export const getConsentHistory = (params) =>
  consentApi.get("/consent/me/history", { params });
